import { request, getApiBase, getActiveTenantId, getActiveOrgEmail } from '@papan/api-client'

// ─── Standalone script runner — client API ──────────────────────────
//
// Thin typed wrapper over POST /api/scripts/run. The server owns the
// security surface (mount equality with the agent, argv/Env passing,
// per-member permission resolution). The client only collects scope +
// data the user supplies; it never builds a shell command.
//
// See docs/plans/2026-05-15_STANDALONE_SCRIPT_RUNNER.md.

export interface ScriptRunArgs {
  fileId: string
  /**
   * Mount root chosen from the script's ancestry ("Run from:").
   * Omitted / null = the script's own parent folder (the default).
   */
  runFromFolderId?: string | null
  /**
   * Raw argv tokens handed to the script. NEVER shell-concatenated —
   * the server passes these as a Docker exec argv array.
   */
  args?: string[]
  /** Literal KEY=VALUE env. Clean base (NOT the agent credentialEnv). */
  env?: Record<string, string>
  /** Optional stdin piped to the process. */
  stdin?: string
  /** Requested wall-clock seconds; the server clamps to its ceiling. */
  timeout?: number
}

export interface ScriptFileChange {
  /** VFS-relative to the chosen mount root. */
  path: string
  change: 'added' | 'modified'
}

export interface ScriptRunResult {
  stdout: string
  stderr: string
  exitCode: number
  durationMs: number
  /** Killed by the `timeout` wrapper (raw exit 124) — shown as a duration, not an exit code. */
  timedOut: boolean
  /** Drive files the run created / modified — the result that matters for automation. */
  filesChanged: ScriptFileChange[]
  /** False when the run-from subtree was too large to snapshot (capped). */
  filesChangedTracked: boolean
  /** The resolved, system-owned interpreter actually invoked (echo of server decision). */
  interpreter: string
  /** D95 revision — `'files_not_saved'`: some files the run wrote were NOT saved to the drive because their access
   *   could not be checked; the run is FAILED whatever its exit code. */
  error?: 'files_not_saved'
  /** …which (bounded; `unsavedTotal` counts all), with what happened to each workspace copy. */
  unsaved?: Array<{ path?: string; stage: string; code: string; disposition: string }>
  unsavedTotal?: number
  /** #874: stdout / stderr came as `done-part` frames before this one (they are empty here). */
  parts?: true
  /** #874: `filesChanged` was cut to fit one stream frame. */
  filesChangedTruncated?: true
}

/**
 * Interpreter inferred from the file extension. Display-only — the
 * authoritative resolution is server-side and system-owned (the user
 * never types, and cannot control, the invocation structure). Keep this
 * map in lock-step with the server's `inferInterpreter`.
 */
export function inferInterpreter(fileName?: string): { interp: string; lang: string } | null {
  const ext = (fileName ?? '').split('.').pop()?.toLowerCase()
  if (ext === 'py') return { interp: 'python3', lang: 'python' }
  if (ext === 'sh' || ext === 'bash') return { interp: 'bash', lang: 'bash' }
  return null
}

/**
 * Split a raw "arguments" line into argv tokens. This is NOT a shell —
 * it only honours single/double quotes so a value with spaces stays one
 * token. Everything else (`;`, `$(…)`, backticks, newlines) is passed
 * through verbatim as literal argv, never evaluated.
 */
export function tokenizeArgs(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let quote: '"' | "'" | null = null
  let started = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (quote) {
      if (ch === quote) quote = null
      else cur += ch
      continue
    }
    if (ch === '"' || ch === "'") { quote = ch; started = true; continue }
    if (ch === ' ' || ch === '\t') {
      if (started) { out.push(cur); cur = ''; started = false }
      continue
    }
    cur += ch
    started = true
  }
  if (started) out.push(cur)
  return out
}

export interface RunFromOption {
  folderId: string
  /** Folder name for the dropdown label. */
  name: string
  /** VFS-relative path, for the "reads & writes inside …" hint. */
  relPath: string
  /** The drive root (#1119): offered like any folder, labelled as the drive names it. Absent from an older server. */
  isDriveRoot?: boolean
}

export interface RunFromOptions {
  /** Script's parent → highest accessible ancestor (default first). */
  options: RunFromOption[]
  /** Pre-selected folderId (the script's own parent when accessible). */
  defaultFolderId: string | null
}

/**
 * The script's accessible ancestry for the "Run from:" control. The
 * server clips the chain at the highest ancestor the member can access
 * and never lists the tenant root (per the plan). The worker still
 * re-enforces per-member permission + ancestry authoritatively.
 */
export async function getRunFromOptions(fileId: string): Promise<RunFromOptions> {
  return await request<RunFromOptions>(`/api/scripts/${encodeURIComponent(fileId)}/run-from`)
}

export class ScriptRunnerError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message)
    this.name = 'ScriptRunnerError'
  }
}

/**
 * Enqueue a run. Returns immediately with the job id (no blocking wait —
 * that was the gateway-timeout bug). The caller then opens an
 * EventSource on `scriptRunStreamUrl(jobId)` for live output.
 */
export async function startScriptRun(args: ScriptRunArgs): Promise<{ jobId: string }> {
  try {
    return await request<{ jobId: string }>('/api/scripts/run', {
      method: 'POST',
      body: JSON.stringify(args),
    })
  } catch (e) {
    throw new ScriptRunnerError(e instanceof Error ? e.message : String(e))
  }
}

/**
 * SSE URL for a run's live stdout/stderr + terminal `done`.
 *
 * An EventSource can't send `x-tenant-id` / `x-org-email`, so the active identity
 * rides as `?tenantId=` / `?orgEmail=` (what `resolveTenant` reads, as the drive's
 * download / streaming URLs do). Without it the stream resolved to the personal
 * tenant and the owner check refused every run started in an org.
 */
export function scriptRunStreamUrl(jobId: string): string {
  const params = new URLSearchParams()
  const tenantId = getActiveTenantId()
  const orgEmail = getActiveOrgEmail()
  if (tenantId) params.set('tenantId', tenantId)
  if (orgEmail) params.set('orgEmail', orgEmail)
  const qs = params.toString()
  return `${getApiBase()}/api/scripts/run/${encodeURIComponent(jobId)}/stream${qs ? `?${qs}` : ''}`
}

/**
 * Explicit Stop — kill a still-running script and free its sandbox slot.
 * Owner-only (server-enforced). Idempotent: a finished/expired run is a no-op
 * success. Closing the live-output stream does NOT stop the run; only this does
 * — so a backgrounded run keeps going and still saves its outputs.
 */
export async function stopScriptRun(jobId: string): Promise<void> {
  try {
    await request(`/api/scripts/run/${encodeURIComponent(jobId)}/abort`, { method: 'POST' })
  } catch (e) {
    throw new ScriptRunnerError(e instanceof Error ? e.message : String(e))
  }
}
